import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BrandingService } from '@next/core-lib/branding';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, Subject, merge, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { Environment } from 'src/environment';

import { CustomerResponse } from '../../customer/models/customer-service.model';
import { Customer } from '../../shared/models/shared.model';
import { Employee, EmployeeListItem, EmployeeRelatedCustomer } from '../models/employee.model';

@Injectable()
export class EmployeeService {
  private readonly http = inject(HttpClient);
  private readonly brandingService = inject(BrandingService);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly employeeBaseUrl = `${this.env.APIM_BASE_URL}/business-partner-v2-service`;
  private readonly customerBaseUrl = `${this.env.API_BASE_URL}/service/customer-account-management/secure`;
  private readonly employeeChangedSubject = new Subject<number>();

  readonly employeeChanged$ = this.employeeChangedSubject.asObservable();

  searchEmployees(query = ''): Observable<EmployeeListItem[]> {
    const searchParams = new URLSearchParams({ tenant: this.getTenant() });
    searchParams.set('query', query);

    return this.http.get<EmployeeListItem[]>(
      `${this.employeeBaseUrl}/management/employees/search?${searchParams.toString()}`,
    );
  }

  getEmployee(employeeId: number): Observable<Employee> {
    return this.http.get<Employee>(
      `${this.employeeBaseUrl}/management/employees/${employeeId}?tenant=${this.getTenant()}`,
    );
  }

  watchEmployee(employeeId$: Observable<number>): Observable<Employee> {
    return employeeId$.pipe(
      switchMap((employeeId) =>
        merge(
          of(employeeId),
          this.employeeChanged$.pipe(
            filter((changedEmployeeId) => changedEmployeeId === employeeId),
            map(() => employeeId),
          ),
        ).pipe(switchMap((currentEmployeeId) => this.getEmployee(currentEmployeeId))),
      ),
    );
  }

  getCustomers(query = ''): Observable<Customer[]> {
    const searchParams = new URLSearchParams({ query });

    return this.http
      .get<CustomerResponse>(`${this.customerBaseUrl}/customers?${searchParams.toString()}`)
      .pipe(map((response) => response.customers));
  }

  getRelatedCustomers(employeeId: number): Observable<EmployeeRelatedCustomer[]> {
    return this.http.get<EmployeeRelatedCustomer[]>(
      `${this.employeeBaseUrl}/management/employees/${employeeId}/customers?tenant=${this.getTenant()}`,
    );
  }

  upsertLocationRelationships(
    employeeId: number,
    customerId: number,
    hasCustomerRelation: boolean,
    locationIds: number[],
  ): Observable<Employee> {
    return this.http.put<Employee>(
      `${this.employeeBaseUrl}/management/employees/${employeeId}/relationships/customers/${customerId}/locations?tenant=${this.getTenant()}`,
      { hasCustomerRelation, relatedLocationIds: locationIds },
    );
  }

  notifyEmployeeChanged(employeeId: number) {
    this.employeeChangedSubject.next(employeeId);
  }

  private getTenant() {
    if (!this.brandingService.isTenant('Agravis')) {
      throw new Error('Employee management requires TENANT');
    }

    return 'Agravis';
  }
}
